// src/components/admin/HelpFaqsManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { helpContentService } from '../../services/helpContentService';
import LoadingSpinner from '../common/LoadingSpinner';
import Toast from '../common/Toast';
import Modal from '../common/Modal';

const emptyForm = { question: '', answer: '', category: 'general', display_order: 0, is_active: 1 };

const HelpFaqsManagement = () => {
    const [faqs, setFaqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingFaq, setEditingFaq] = useState(null);
    const [formData, setFormData] = useState(emptyForm);
    const [formErrors, setFormErrors] = useState({});
    const [saveLoading, setSaveLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    const showToast = (message, type = 'success') => setToast({ show: true, message, type });

    const loadFaqs = useCallback(async () => {
        try {
            setLoading(true);
            const response = await helpContentService.getHelpFaqs();
            const list = response.help_faqs || [];
            list.sort((a, b) => (Number(a.display_order) || 0) - (Number(b.display_order) || 0));
            setFaqs(list);
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadFaqs();
    }, [loadFaqs]);

    const handleAddClick = () => {
        setEditingFaq(null);
        setFormData({ ...emptyForm, display_order: faqs.length + 1 });
        setFormErrors({});
        setShowModal(true);
    };

    const handleEditClick = (faq) => {
        setEditingFaq(faq);
        setFormData({
            question: faq.question || '',
            answer: faq.answer || '',
            category: faq.category || 'general',
            display_order: Number(faq.display_order) || 0,
            is_active: Number(faq.is_active) ? 1 : 0
        });
        setFormErrors({});
        setShowModal(true);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.question.trim()) errors.question = 'Question is required';
        if (!formData.answer.trim()) errors.answer = 'Answer is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            setSaveLoading(true);
            const payload = {
                ...formData,
                display_order: Number(formData.display_order) || 0,
                action: editingFaq ? 'update' : 'create'
            };
            if (editingFaq) {
                payload.id = editingFaq.id;
            }

            await helpContentService.updateHelpContent({ faqs: [payload] });
            showToast(editingFaq ? 'FAQ updated successfully' : 'FAQ created successfully', 'success');
            setShowModal(false);
            loadFaqs();
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setSaveLoading(false);
        }
    };

    const handleDelete = async (faq) => {
        if (!window.confirm('Are you sure you want to delete this FAQ?')) return;

        try {
            await helpContentService.updateHelpContent({ faqs: [{ id: faq.id, action: 'delete' }] });
            showToast('FAQ deleted successfully', 'success');
            loadFaqs();
        } catch (error) {
            showToast(error.message, 'error');
        }
    };

    return (
        <div>
            <Toast
                show={toast.show}
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ show: false, message: '', type: 'success' })}
            />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                        Frequently Asked Questions
                    </h2>
                    <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '13px' }}>
                        Manage the FAQs shown in the mobile app's Help section
                    </p>
                </div>
                <button
                    onClick={handleAddClick}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#007bff',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <i className="fas fa-plus"></i>
                    Add FAQ
                </button>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <LoadingSpinner />
                </div>
            ) : faqs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    <i className="fas fa-question-circle" style={{ fontSize: '48px', marginBottom: '15px', opacity: 0.3 }}></i>
                    <h3>No FAQs found</h3>
                    <p>Add a FAQ to show it in the mobile app</p>
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        overflow: 'hidden'
                    }}>
                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                            <tr>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>Order</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>Question</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>Category</th>
                                <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#333', fontSize: '13px' }}>Status</th>
                                <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#333', fontSize: '13px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {faqs.map((faq) => (
                                <tr key={faq.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                                    <td style={{ padding: '12px', fontSize: '14px' }}>{faq.display_order}</td>
                                    <td style={{ padding: '12px', fontSize: '14px', fontWeight: '500', maxWidth: '400px' }}>{faq.question}</td>
                                    <td style={{ padding: '12px', fontSize: '14px' }}>{faq.category}</td>
                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                        <span style={{
                                            backgroundColor: Number(faq.is_active) ? '#28a745' : '#dc3545',
                                            color: '#fff',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            fontWeight: '600'
                                        }}>
                                            {Number(faq.is_active) ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                        <button
                                            onClick={() => handleEditClick(faq)}
                                            style={{
                                                padding: '6px 12px',
                                                backgroundColor: '#007bff',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                                marginRight: '8px'
                                            }}
                                        >
                                            <i className="fas fa-edit"></i> Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(faq)}
                                            style={{
                                                padding: '6px 12px',
                                                backgroundColor: '#dc3545',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '13px'
                                            }}
                                        >
                                            <i className="fas fa-trash"></i> Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={editingFaq ? 'Edit FAQ' : 'Add FAQ'}
                size="medium"
            >
                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                            Question <span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                            type="text"
                            name="question"
                            value={formData.question}
                            onChange={handleInputChange}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: formErrors.question ? '1px solid #dc3545' : '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                            placeholder="e.g. How do I request leave?"
                        />
                        {formErrors.question && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{formErrors.question}</div>}
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                            Answer <span style={{ color: 'red' }}>*</span>
                        </label>
                        <textarea
                            name="answer"
                            value={formData.answer}
                            onChange={handleInputChange}
                            rows={6}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: formErrors.answer ? '1px solid #dc3545' : '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px',
                                fontFamily: 'inherit',
                                resize: 'vertical'
                            }}
                            placeholder="Answer shown when the user expands this FAQ"
                        />
                        {formErrors.answer && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{formErrors.answer}</div>}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                                Category
                            </label>
                            <input
                                type="text"
                                name="category"
                                value={formData.category}
                                onChange={handleInputChange}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '14px'
                                }}
                                placeholder="e.g. general"
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                                Display Order
                            </label>
                            <input
                                type="number"
                                name="display_order"
                                value={formData.display_order}
                                onChange={handleInputChange}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '14px'
                                }}
                            />
                        </div>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                name="is_active"
                                checked={formData.is_active === 1}
                                onChange={handleInputChange}
                                style={{ marginRight: '8px' }}
                            />
                            <span style={{ fontWeight: '500', fontSize: '14px' }}>Active (visible in the mobile app)</span>
                        </label>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
                        <button
                            type="button"
                            onClick={() => setShowModal(false)}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#6c757d',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                            disabled={saveLoading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#007bff',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: saveLoading ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                opacity: saveLoading ? 0.7 : 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                            disabled={saveLoading}
                        >
                            {saveLoading && <LoadingSpinner size="small" />}
                            {saveLoading ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default HelpFaqsManagement;
